import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Check, Pencil, Plus, Search, Trash2 } from 'lucide-react';
import {
  PERSON_KIND_LABELS,
  PERSON_ROLES,
  PERSON_ROLE_LABELS,
  PIX_KEY_KINDS,
  PIX_KEY_KIND_LABELS,
  type Paginated,
  type Person,
  type PersonKind,
  type PersonRole,
  type PixKeyKind,
} from '@amai/contracts';
import { api } from '@/lib/api';
import { errorMessage, fieldErrors } from '@/lib/errors';
import { blank, formatCep, formatDate, formatDocument, formatPhone } from '@/lib/format';
import { T } from '@/design/tokens';
import { WorkspaceHeader } from '@/components/WorkspaceHeader';
import { FilterBar } from '@/components/FilterBar';
import { SmartDataTable, type Column } from '@/components/SmartDataTable';
import { ContextPanel, ReadField } from '@/components/ContextPanel';
import { Attachments } from '@/components/Attachments';
import { ActiveBadge } from '@/components/StatusBadge';
import { Button } from '@/components/Button';
import { MaskedInput } from '@/components/MaskedInput';
import { Drawer, ConfirmDialog } from '@/components/Modal';
import { CollapsibleSection, Field, FormSection } from '@/components/FormSection';
import { EmptyState, ErrorState, NoResultsState, TableSkeleton } from '@/components/states';
import { useAuth } from '@/lib/auth-context';
import { fillIfEmpty, useCepLookup, useCnpjLookup } from '@/lib/lookup';

/**
 * Cadastro único de pessoas.
 *
 * Fornecedor, cliente e colaborador são PAPÉIS do mesmo registro — é por isso
 * que a tela é uma só e o filtro por papel fica na barra, não em telas
 * separadas que duplicariam o mesmo CNPJ três vezes.
 */
export function PessoasPage() {
  const { can } = useAuth();
  const queryClient = useQueryClient();

  const [search, setSearch] = useState('');
  const [role, setRole] = useState('');
  const [kind, setKind] = useState('');
  const [page, setPage] = useState(1);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [editing, setEditing] = useState<Person | null>(null);
  const [creating, setCreating] = useState(false);
  const [removing, setRemoving] = useState(false);

  const listQuery = useQuery({
    queryKey: ['people', { search, role, kind, page }],
    queryFn: () =>
      api.get<Paginated<Person>>('/people', {
        search: search || undefined,
        role: role || undefined,
        kind: kind || undefined,
        page,
        perPage: 25,
      }),
  });

  const detailQuery = useQuery({
    queryKey: ['person', selectedId],
    queryFn: () => api.get<Person>(`/people/${selectedId}`),
    enabled: Boolean(selectedId),
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['people'] });
    void queryClient.invalidateQueries({ queryKey: ['person'] });
  };

  const removeMutation = useMutation({
    mutationFn: () => api.delete(`/people/${selectedId}`),
    onSuccess: () => {
      setRemoving(false);
      setSelectedId(null);
      refresh();
    },
  });

  const person = detailQuery.data;

  const columns: Array<Column<Person>> = [
    { key: 'name', label: 'Nome / razão social', strong: true, sortable: true, render: (row) => row.name },
    {
      key: 'document',
      label: 'CPF / CNPJ',
      mono: true,
      render: (row) => (row.document ? formatDocument(row.document) : '—'),
    },
    {
      key: 'roles',
      label: 'Papéis',
      // Papel é classificação, não estado. Como selo colorido, "Fornecedor"
      // disputava atenção com o vencido e o pago em toda a tela.
      render: (row) => row.roles.map((item) => PERSON_ROLE_LABELS[item]).join(' · '),
    },
    { key: 'city', label: 'Cidade', render: (row) => (row.city ? `${row.city}/${row.uf ?? ''}` : '—') },
    {
      key: 'phone',
      label: 'Telefone',
      mono: true,
      render: (row) => (row.phone ? formatPhone(row.phone) : '—'),
    },
    { key: 'isActive', label: 'Situação', render: (row) => <ActiveBadge active={row.isActive} /> },
    ...(can('pessoas:editar')
      ? [
          {
            key: 'actions',
            label: 'Ações',
            width: 96,
            // Editar direto da linha: abrir o painel só para achar o botão é
            // um clique a mais, e quem cadastra fornecedor faz isso o dia todo.
            render: (row: Person) => (
              <Button
                small
                icon={Pencil}
                aria-label={`Editar ${row.name}`}
                onClick={(event) => {
                  event.stopPropagation();
                  setEditing(row);
                }}
              >
                Editar
              </Button>
            ),
          } satisfies Column<Person>,
        ]
      : []),
  ];

  const hasFilters = Boolean(search || role || kind);

  return (
    <>
      <WorkspaceHeader
        title="Pessoas"
        actions={
          can('pessoas:criar') ? (
            <Button variant="primary" icon={Plus} onClick={() => setCreating(true)}>
              Nova pessoa
            </Button>
          ) : undefined
        }
      />

      <div className="workspace__body">
        <FilterBar
          placeholder="Buscar por nome, documento, e-mail ou cidade…"
          search={search}
          onSearchChange={(value) => {
            setSearch(value);
            setPage(1);
          }}
          filters={[
            {
              key: 'role',
              label: 'Papel',
              value: role,
              options: PERSON_ROLES.map((value) => ({ value, label: PERSON_ROLE_LABELS[value] })),
              onChange: (value) => {
                setRole(value);
                setPage(1);
              },
            },
            {
              key: 'kind',
              label: 'Tipo',
              value: kind,
              options: Object.entries(PERSON_KIND_LABELS).map(([value, label]) => ({ value, label })),
              onChange: (value) => {
                setKind(value);
                setPage(1);
              },
            },
          ]}
        />

        {listQuery.isPending && <TableSkeleton columns={6} />}
        {listQuery.isError && (
          <ErrorState message={errorMessage(listQuery.error)} onRetry={() => void listQuery.refetch()} />
        )}
        {listQuery.data && (
          <SmartDataTable
            caption="Pessoas cadastradas"
            columns={columns}
            rows={listQuery.data.data}
            meta={listQuery.data.meta}
            selectedId={selectedId}
            onRowClick={(row) => setSelectedId(row.id)}
            onPageChange={setPage}
            emptyState={
              hasFilters ? (
                <NoResultsState
                  onClear={() => {
                    setSearch('');
                    setRole('');
                    setKind('');
                  }}
                />
              ) : (
                <EmptyState
                  title="Nenhuma pessoa cadastrada"
                  description="Cadastre os fornecedores para começar a lançar compras e notas."
                  action={
                    can('pessoas:criar') ? (
                      <Button variant="primary" icon={Plus} onClick={() => setCreating(true)}>
                        Nova pessoa
                      </Button>
                    ) : undefined
                  }
                />
              )
            }
          />
        )}
      </div>

      <ContextPanel
        open={Boolean(selectedId)}
        onClose={() => setSelectedId(null)}
        title={person?.name ?? 'Carregando…'}
        subtitle={person?.document ? formatDocument(person.document) : undefined}
        tabs={['Dados', 'Endereço', 'Pagamento', 'Anexos', 'Histórico']}
        footer={
          person ? (
            <>
              {can('pessoas:editar') && (
                <Button icon={Pencil} small onClick={() => setEditing(person)}>
                  Editar
                </Button>
              )}
              {can('pessoas:excluir') && (
                <Button variant="danger" icon={Trash2} small onClick={() => setRemoving(true)}>
                  Excluir
                </Button>
              )}
            </>
          ) : undefined
        }
      >
        {(tab) => {
          if (detailQuery.isPending) return <p className="muted">Carregando…</p>;
          if (!person) return <p className="muted">Não foi possível carregar.</p>;

          if (tab === 0) {
            return (
              <>
                <ReadField label="Tipo" value={PERSON_KIND_LABELS[person.kind]} />
                <ReadField label="Nome fantasia / apelido" value={blank(person.tradeName)} />
                <ReadField
                  label="Papéis"
                  value={person.roles.map((item) => PERSON_ROLE_LABELS[item]).join(' · ')}
                />
                <ReadField label="E-mail" value={blank(person.email)} />
                <ReadField
                  label="Telefone"
                  value={person.phone ? formatPhone(person.phone) : '—'}
                  mono
                />
                <ReadField
                  label="WhatsApp"
                  value={person.whatsapp ? formatPhone(person.whatsapp) : '—'}
                  mono
                />
                <ReadField label="Contato" value={blank(person.contactName)} />
                <ReadField label="Inscrição estadual" value={blank(person.stateRegistration)} mono />
                <ReadField label="Situação" value={<ActiveBadge active={person.isActive} />} />
                {person.notes && <ReadField label="Observações" value={person.notes} />}
              </>
            );
          }

          if (tab === 1) {
            return (
              <>
                <ReadField label="CEP" value={person.zip ? formatCep(person.zip) : "—"} mono />
                <ReadField
                  label="Logradouro"
                  value={`${blank(person.street)}${person.number ? `, ${person.number}` : ''}`}
                />
                <ReadField label="Complemento" value={blank(person.complement)} />
                <ReadField label="Bairro" value={blank(person.district)} />
                <ReadField
                  label="Cidade / UF"
                  value={person.city ? `${person.city}/${person.uf ?? ''}` : '—'}
                />
              </>
            );
          }

          if (tab === 2) {
            const semDados =
              !person.bankName && !person.bankAccount && !person.pixKey;
            return semDados ? (
              <p className="muted" style={{ fontSize: 13 }}>
                Sem banco nem PIX cadastrado. Na hora de pagar, alguém vai ter de
                perguntar ao fornecedor — vale preencher agora.
              </p>
            ) : (
              <>
                <ReadField label="Banco" value={blank(person.bankName)} />
                <ReadField label="Agência" value={blank(person.bankAgency)} mono />
                <ReadField label="Conta" value={blank(person.bankAccount)} mono />
                <ReadField label="Chave PIX" value={blank(person.pixKey)} mono />
                <ReadField
                  label="Tipo da chave"
                  value={person.pixKeyKind ? PIX_KEY_KIND_LABELS[person.pixKeyKind] : '—'}
                />
              </>
            );
          }

          if (tab === 3) {
            return (
              <Attachments
                entity="Person"
                entityId={person.id}
                canEdit={can('pessoas:editar')}
              />
            );
          }

          return (
            <>
              <ReadField label="Cadastrado em" value={formatDate(person.createdAt)} mono />
              <ReadField label="Última alteração" value={formatDate(person.updatedAt)} mono />
              <p className="muted" style={{ fontSize: 12.5, marginTop: 12, color: T.inkFaint }}>
                Quem alterou e o que mudou fica na trilha de auditoria, em
                Configurações › Trilha de auditoria.
              </p>
            </>
          );
        }}
      </ContextPanel>

      <PersonFormDrawer
        open={creating || Boolean(editing)}
        person={editing}
        onClose={() => {
          setCreating(false);
          setEditing(null);
        }}
        onSaved={() => {
          setCreating(false);
          setEditing(null);
          refresh();
        }}
      />

      <ConfirmDialog
        open={removing}
        destructive
        loading={removeMutation.isPending}
        title="Excluir esta pessoa?"
        confirmLabel="Excluir"
        message={
          <>
            O cadastro sai das listas, mas o histórico de compras, notas e
            lançamentos continua intacto — nada do que já aconteceu é apagado.
            {removeMutation.error && (
              <div style={{ color: T.red, marginTop: 10 }}>{errorMessage(removeMutation.error)}</div>
            )}
          </>
        }
        onClose={() => {
          setRemoving(false);
          removeMutation.reset();
        }}
        onConfirm={() => removeMutation.mutate()}
      />
    </>
  );
}

/**
 * Formulário de pessoa: o essencial primeiro, endereço e fiscal sob demanda.
 *
 * Também serve a tela de Fornecedores, no Financeiro — é o MESMO cadastro,
 * só muda o nome na porta e o papel que já vem marcado.
 */
export function PersonFormDrawer({
  open,
  person,
  onClose,
  onSaved,
  noun = 'pessoa',
  defaultRoles = ['FORNECEDOR'],
}: {
  open: boolean;
  person: Person | null;
  onClose: () => void;
  onSaved: () => void;
  /** Como a tela chama o registro: "pessoa" ou "fornecedor". */
  noun?: 'pessoa' | 'fornecedor';
  /** Papéis já marcados num cadastro novo. */
  defaultRoles?: PersonRole[];
}) {
  const [kind, setKind] = useState<PersonKind>('JURIDICA');
  const [name, setName] = useState('');
  const [tradeName, setTradeName] = useState('');
  const [document, setDocument] = useState('');
  const [roles, setRoles] = useState<PersonRole[]>(defaultRoles);
  const [bankName, setBankName] = useState('');
  const [bankAgency, setBankAgency] = useState('');
  const [bankAccount, setBankAccount] = useState('');
  const [pixKey, setPixKey] = useState('');
  const [pixKeyKind, setPixKeyKind] = useState<PixKeyKind | ''>('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [whatsapp, setWhatsapp] = useState('');
  const [contactName, setContactName] = useState('');
  const [stateRegistration, setStateRegistration] = useState('');
  const [zip, setZip] = useState('');
  const [street, setStreet] = useState('');
  const [number, setNumber] = useState('');
  const [complement, setComplement] = useState('');
  const [district, setDistrict] = useState('');
  const [city, setCity] = useState('');
  const [uf, setUf] = useState('AC');
  const [notes, setNotes] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [loadedFor, setLoadedFor] = useState<string | null>(null);

  // Abrir (ou trocar de registro) recarrega o formulário. Ajuste durante o
  // render em vez de efeito: sem piscar os valores do registro anterior.
  const formKey = open ? (person?.id ?? 'new') : null;
  if (formKey !== loadedFor) {
    setLoadedFor(formKey);
    setKind(person?.kind ?? 'JURIDICA');
    setName(person?.name ?? '');
    setTradeName(person?.tradeName ?? '');
    setDocument(person?.document ?? '');
    setRoles(person?.roles ?? defaultRoles);
    setBankName(person?.bankName ?? '');
    setBankAgency(person?.bankAgency ?? '');
    setBankAccount(person?.bankAccount ?? '');
    setPixKey(person?.pixKey ?? '');
    setPixKeyKind(person?.pixKeyKind ?? '');
    setEmail(person?.email ?? '');
    setPhone(person?.phone ?? '');
    setWhatsapp(person?.whatsapp ?? '');
    setContactName(person?.contactName ?? '');
    setStateRegistration(person?.stateRegistration ?? '');
    setZip(person?.zip ?? '');
    setStreet(person?.street ?? '');
    setNumber(person?.number ?? '');
    setComplement(person?.complement ?? '');
    setDistrict(person?.district ?? '');
    setCity(person?.city ?? '');
    setUf(person?.uf ?? 'AC');
    setNotes(person?.notes ?? '');
    setIsActive(person?.isActive ?? true);
  }

  const mutation = useMutation({
    mutationFn: (body: unknown) =>
      person ? api.patch(`/people/${person.id}`, body) : api.post('/people', body),
    onSuccess: onSaved,
  });

  const cnpj = useCnpjLookup();
  const cep = useCepLookup();

  /**
   * CNPJ completo traz o cadastro da Receita e preenche só o que está VAZIO —
   * o comprador digita 14 dígitos em vez de a ficha inteira, e ainda vê se o
   * fornecedor está ATIVO antes de comprar dele.
   */
  const puxarCnpj = (value: string) => {
    if (value.length !== 14 || kind !== 'JURIDICA') return;
    cnpj.mutate(value, {
      onSuccess: (data) => {
        setName((v) => fillIfEmpty(v, data.name));
        setTradeName((v) => fillIfEmpty(v, data.tradeName));
        setEmail((v) => fillIfEmpty(v, data.email));
        setPhone((v) => fillIfEmpty(v, data.phone));
        setZip((v) => fillIfEmpty(v, data.zip));
        setStreet((v) => fillIfEmpty(v, data.street));
        setNumber((v) => fillIfEmpty(v, data.number));
        setDistrict((v) => fillIfEmpty(v, data.district));
        setCity((v) => fillIfEmpty(v, data.city));
        setUf((v) => fillIfEmpty(v, data.uf));
      },
    });
  };

  const puxarCep = (value: string) => {
    if (value.length !== 8) return;
    cep.mutate(value, {
      onSuccess: (data) => {
        setStreet((v) => fillIfEmpty(v, data.street));
        setDistrict((v) => fillIfEmpty(v, data.district));
        setCity((v) => fillIfEmpty(v, data.city));
        setUf((v) => fillIfEmpty(v, data.uf));
      },
    });
  };

  const errors = fieldErrors(mutation.error);
  const digits = document.replace(/\D/g, '');
  const documentOk = digits === '' || (kind === 'JURIDICA' ? digits.length === 14 : digits.length === 11);
  const canSubmit = name.trim().length >= 2 && roles.length > 0 && documentOk;

  const toggleRole = (value: PersonRole) =>
    setRoles((current) =>
      current.includes(value) ? current.filter((item) => item !== value) : [...current, value],
    );

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={person ? `Editar ${noun}` : noun === 'fornecedor' ? 'Novo fornecedor' : 'Nova pessoa'}
      description="Comece pelo documento — o resto pode vir depois"
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={mutation.isPending}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            disabled={!canSubmit}
            loading={mutation.isPending}
            onClick={() =>
              mutation.mutate({
                kind,
                name: name.trim(),
                tradeName: tradeName.trim() || null,
                document: digits || null,
                roles,
                bankName: bankName.trim() || null,
                bankAgency: bankAgency.trim() || null,
                bankAccount: bankAccount.trim() || null,
                pixKey: pixKey.trim() || null,
                pixKeyKind: pixKey.trim() ? pixKeyKind || null : null,
                email: email.trim() || null,
                phone: phone.replace(/\D/g, '') || null,
                whatsapp: whatsapp.replace(/\D/g, '') || null,
                contactName: contactName.trim() || null,
                stateRegistration: stateRegistration.trim() || null,
                zip: zip.replace(/\D/g, '') || null,
                street: street.trim() || null,
                number: number.trim() || null,
                complement: complement.trim() || null,
                district: district.trim() || null,
                city: city.trim() || null,
                uf: uf.trim() ? uf.trim().toUpperCase() : null,
                notes: notes.trim() || null,
                isActive,
              })
            }
          >
            {person ? 'Salvar' : 'Cadastrar'}
          </Button>
        </>
      }
    >
      {mutation.isError && (
        <div className="login__alert" role="alert" style={{ marginBottom: 14 }}>
          {errorMessage(mutation.error)}
        </div>
      )}

      <FormSection>
        <Field label="Tipo" htmlFor="person-kind">
          <select
            id="person-kind"
            className="input"
            value={kind}
            onChange={(event) => setKind(event.target.value as PersonKind)}
          >
            {Object.entries(PERSON_KIND_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </Field>

        <Field
          label={kind === 'JURIDICA' ? 'CNPJ' : 'CPF'}
          htmlFor="person-document"
          error={errors.document ?? (documentOk ? undefined : 'Documento incompleto para este tipo')}
          hint={
            kind !== 'JURIDICA'
              ? 'Só dígitos ou com pontuação — nós normalizamos'
              : cnpj.isPending
                ? 'Consultando a Receita…'
                : cnpj.isError
                  ? 'CNPJ não encontrado na Receita — preencha à mão'
                  : cnpj.isSuccess
                    ? `Dados da Receita aplicados · situação ${cnpj.data.status ?? '—'}`
                    : 'Ao completar o CNPJ, buscamos os dados na Receita'
          }
        >
          <div style={{ display: 'flex', gap: 8 }}>
            <MaskedInput
              id="person-document"
              mask={kind === 'JURIDICA' ? 'document' : 'cpf'}
              value={document}
              onChange={(value) => {
                setDocument(value);
                puxarCnpj(value);
              }}
            />
            {kind === 'JURIDICA' && (
              <Button
                small
                icon={cnpj.isSuccess ? Check : Search}
                loading={cnpj.isPending}
                disabled={digits.length !== 14}
                aria-label="Buscar dados do CNPJ na Receita"
                onClick={() => puxarCnpj(digits)}
              />
            )}
          </div>
        </Field>

        {cnpj.isSuccess && !cnpj.data.isActive && (
          <div className="attention__item attention__item--bad" style={{ display: 'flex', marginBottom: 12 }}>
            <AlertTriangle size={15} color={T.red} aria-hidden />
            <span className="attention__text">
              Atenção: na Receita este CNPJ está <strong>{cnpj.data.status}</strong>, não ATIVA.
              Confira antes de comprar ou pagar.
            </span>
          </div>
        )}

        <Field
          label={kind === 'JURIDICA' ? 'Razão social' : 'Nome completo'}
          htmlFor="person-name"
          error={errors.name}
        >
          <input
            id="person-name"
            className="input"
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
        </Field>

        <Field label="Papéis" error={errors.roles}>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {PERSON_ROLES.map((value) => (
              <button
                key={value}
                type="button"
                className={`filterbar__chip${roles.includes(value) ? ' is-active' : ''}`}
                onClick={() => toggleRole(value)}
                aria-pressed={roles.includes(value)}
              >
                {PERSON_ROLE_LABELS[value]}
              </button>
            ))}
          </div>
        </Field>

        <div className="grid-2">
          <Field label="Telefone" htmlFor="person-phone" error={errors.phone}>
            <MaskedInput id="person-phone" mask="phone" value={phone} onChange={setPhone} />
          </Field>
          <Field label="E-mail" htmlFor="person-email" error={errors.email}>
            <input
              id="person-email"
              className="input"
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
          </Field>
        </div>
      </FormSection>

      <CollapsibleSection label="Mais informações (fantasia, contato, fiscal)">
        <Field label={kind === 'JURIDICA' ? 'Nome fantasia' : 'Apelido'} htmlFor="person-trade">
          <input
            id="person-trade"
            className="input"
            value={tradeName}
            onChange={(event) => setTradeName(event.target.value)}
          />
        </Field>
        <div className="grid-2">
          <Field label="Contato" htmlFor="person-contact">
            <input
              id="person-contact"
              className="input"
              value={contactName}
              onChange={(event) => setContactName(event.target.value)}
            />
          </Field>
          <Field label="WhatsApp" htmlFor="person-whats">
            <input
              id="person-whats"
              className="input input--mono"
              value={whatsapp}
              onChange={(event) => setWhatsapp(event.target.value)}
            />
          </Field>
        </div>
        <Field label="Inscrição estadual" htmlFor="person-ie">
          <input
            id="person-ie"
            className="input input--mono"
            value={stateRegistration}
            onChange={(event) => setStateRegistration(event.target.value)}
          />
        </Field>
      </CollapsibleSection>

      <CollapsibleSection label="Endereço">
        <div className="grid-2">
          <Field
            label="CEP"
            htmlFor="person-zip"
            error={errors.zip}
            hint={cep.isPending ? 'Buscando o endereço…' : 'Ao completar, buscamos o endereço'}
          >
            <MaskedInput
              id="person-zip"
              mask="cep"
              value={zip}
              onChange={(value) => {
                setZip(value);
                puxarCep(value);
              }}
            />
          </Field>
          <Field label="Número" htmlFor="person-number">
            <input
              id="person-number"
              className="input input--mono"
              value={number}
              onChange={(event) => setNumber(event.target.value)}
            />
          </Field>
        </div>
        <Field label="Logradouro" htmlFor="person-street">
          <input
            id="person-street"
            className="input"
            value={street}
            onChange={(event) => setStreet(event.target.value)}
          />
        </Field>
        <div className="grid-2">
          <Field label="Complemento" htmlFor="person-complement">
            <input
              id="person-complement"
              className="input"
              value={complement}
              onChange={(event) => setComplement(event.target.value)}
            />
          </Field>
          <Field label="Bairro" htmlFor="person-district">
            <input
              id="person-district"
              className="input"
              value={district}
              onChange={(event) => setDistrict(event.target.value)}
            />
          </Field>
        </div>
        <div className="grid-2">
          <Field label="Cidade" htmlFor="person-city">
            <input
              id="person-city"
              className="input"
              value={city}
              onChange={(event) => setCity(event.target.value)}
            />
          </Field>
          <Field label="UF" htmlFor="person-uf" error={errors.uf}>
            <input
              id="person-uf"
              className="input input--mono"
              maxLength={2}
              value={uf}
              onChange={(event) => setUf(event.target.value.toUpperCase())}
            />
          </Field>
        </div>
      </CollapsibleSection>

      <CollapsibleSection label="Dados para pagamento (banco e PIX)">
        <div className="grid-2">
          <Field label="Banco" htmlFor="person-bank">
            <input
              id="person-bank"
              className="input"
              value={bankName}
              placeholder="Banco do Brasil"
              onChange={(event) => setBankName(event.target.value)}
            />
          </Field>
          <Field label="Agência" htmlFor="person-agency">
            <input
              id="person-agency"
              className="input input--mono"
              value={bankAgency}
              placeholder="1234-5"
              onChange={(event) => setBankAgency(event.target.value)}
            />
          </Field>
        </div>
        <Field label="Conta" htmlFor="person-account">
          <input
            id="person-account"
            className="input input--mono"
            value={bankAccount}
            placeholder="12345-6"
            onChange={(event) => setBankAccount(event.target.value)}
          />
        </Field>
        <div className="grid-2">
          <Field label="Chave PIX" htmlFor="person-pix" error={errors.pixKey}>
            <input
              id="person-pix"
              className="input input--mono"
              value={pixKey}
              onChange={(event) => setPixKey(event.target.value)}
            />
          </Field>
          <Field
            label="Tipo da chave"
            htmlFor="person-pix-kind"
            error={errors.pixKeyKind}
            hint={pixKey.trim() && !pixKeyKind ? 'Diga o tipo para o operador não errar na hora de pagar' : undefined}
          >
            <select
              id="person-pix-kind"
              className="input"
              value={pixKeyKind}
              onChange={(event) => setPixKeyKind(event.target.value as PixKeyKind | '')}
            >
              <option value="">Selecione…</option>
              {PIX_KEY_KINDS.map((value) => (
                <option key={value} value={value}>
                  {PIX_KEY_KIND_LABELS[value]}
                </option>
              ))}
            </select>
          </Field>
        </div>
      </CollapsibleSection>

      <CollapsibleSection label="Observações e situação">
        <Field label="Observações" htmlFor="person-notes">
          <textarea
            id="person-notes"
            className="input"
            rows={3}
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
          />
        </Field>
        <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13 }}>
          <input
            type="checkbox"
            checked={isActive}
            onChange={(event) => setIsActive(event.target.checked)}
          />
          Cadastro ativo
        </label>
      </CollapsibleSection>
    </Drawer>
  );
}
